import { parseISODate, type ISODate, type ISOMonth } from './calendar';

// Intl formatters are comparatively expensive to construct; cache them by options.
const formatters = new Map<string, Intl.DateTimeFormat>();

export function safeLocale(locale: string | undefined): string {
  if (!locale) return 'en';
  try {
    return Intl.getCanonicalLocales(locale)[0] ?? 'en';
  } catch {
    return 'en';
  }
}

function dtf(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = locale + JSON.stringify(options);
  let f = formatters.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat(safeLocale(locale), options);
    } catch {
      f = new Intl.DateTimeFormat('en', { ...options, timeZone: 'UTC' });
    }
    formatters.set(key, f);
  }
  return f;
}

const toDate = (value: string | number | Date): Date =>
  value instanceof Date ? value : new Date(value);

const uses24h = new Map<string, boolean>();

/** `9:00 AM` (12-hour locales) / `09:00` (24-hour locales) in `timeZone`. */
export function formatTime(
  instant: string | number | Date,
  locale: string,
  timeZone: string
): string {
  let h24 = uses24h.get(locale);
  if (h24 === undefined) {
    h24 = dtf(locale, { hour: 'numeric', minute: '2-digit' }).resolvedOptions().hour12 === false;
    uses24h.set(locale, h24);
  }
  // 24-hour locales expect a leading zero (`09:00`); 12-hour ones read better without (`9:00 AM`).
  return dtf(locale, { timeZone, hour: h24 ? '2-digit' : 'numeric', minute: '2-digit' }).format(
    toDate(instant)
  );
}

/** `9:00 AM – 9:30 AM` — start and end formatted separately so both carry the day period. */
export function formatTimeRange(
  start: string | number | Date,
  end: string | number | Date,
  locale: string,
  timeZone: string
): string {
  return `${formatTime(start, locale, timeZone)} – ${formatTime(end, locale, timeZone)}`;
}

/** `Monday, March 30` for a calendar date (no zone involved). */
export function formatDayLong(date: ISODate, locale: string, withYear = false): string {
  const [y, m, d] = parseISODate(date);
  return dtf(locale, {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** `Monday, March 30, 2026` for an instant, as seen in `timeZone`. */
export function formatInstantDay(
  instant: string | number | Date,
  locale: string,
  timeZone: string
): string {
  return dtf(locale, {
    timeZone,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(toDate(instant));
}

/** `Monday, March 30, 10:00 AM` */
export function formatInstantDateTime(
  instant: string | number | Date,
  locale: string,
  timeZone: string
): string {
  return dtf(locale, {
    timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(toDate(instant));
}

export function formatMonthTitle(month: ISOMonth, locale: string): string {
  const [y, m] = parseISODate(`${month}-01`);
  return dtf(locale, { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(y, m - 1, 1))
  );
}

/** `GMT+2`, `GMT+5:30`, … for `timeZone` at `at`. */
export function formatZoneOffset(
  timeZone: string,
  locale: string,
  at: number | Date = Date.now()
): string {
  try {
    const parts = dtf(locale, { timeZone, timeZoneName: 'shortOffset' }).formatToParts(toDate(at));
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** `Europe/Zurich` → `Europe/Zurich (GMT+2)` */
export function formatZoneLabel(timeZone: string, locale: string): string {
  const offset = formatZoneOffset(timeZone, locale);
  const name = timeZone.replace(/_/g, ' ');
  return offset ? `${name} (${offset})` : name;
}

/** Minutes → `90 minutes`, `24 hours`, `2 days` (localised units). */
export function formatMinutes(minutes: number, locale: string): string {
  const [unit, value] =
    minutes % 1440 === 0 && minutes >= 1440
      ? ['day', minutes / 1440]
      : minutes % 60 === 0 && minutes >= 60
        ? ['hour', minutes / 60]
        : ['minute', minutes];
  try {
    return new Intl.NumberFormat(safeLocale(locale), {
      style: 'unit',
      unit,
      unitDisplay: 'long',
    }).format(value);
  } catch {
    return `${value} ${unit}${value === 1 ? '' : 's'}`;
  }
}

/** Seconds → `m:ss`. */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const rest = s % 60;
  return `${Math.floor(s / 60)}:${rest < 10 ? '0' : ''}${rest}`;
}

/** Localised "and"-list: `Anna and Ben`, `Anna, Ben und Chris`. */
export function formatList(items: string[], locale: string): string {
  try {
    return new Intl.ListFormat(safeLocale(locale), { type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}
